import { hash } from '@ember/helper';
import { type Registry as Services, service } from '@ember/service';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import {
  PrismButton,
  PrismCard,
  PrismInput,
  PrismMessage,
  PrismTable,
} from '@mylighthouse/prism-ember';
import type { CustomEmberTableColumn } from '@mylighthouse/prism-ember/types/components/prism/table';
import { task } from 'ember-concurrency';
import { t } from 'ember-intl';
import SettingsCostTablesCellsTdTypes from 'lumen/components/settings/cost/tables/cells/td-types';
import SettingsCostTablesCellsTdWithActions from 'lumen/components/settings/cost/tables/cells/td-with-actions';
import SettingsPageTemplate from 'lumen/components/settings/page-template';
import type { CostSettingProfile } from 'lumen/models';
import { timeout } from 'lumen/utils/waiters';

const COLUMNS = {
  TITLE: 'title',
  TYPE: 'type',
  CREATED: 'created',
  CREATED_DISPLAY: 'createdDisplay',
  LAST_EDITED: 'lastEdited',
  LAST_EDITED_DISPLAY: 'lastEditedDisplay',
  ACTIONS: 'actions',
} as const;

const DATE_FORMAT = 'DD MMM YYYY';

type Row = {
  [COLUMNS.TITLE]: string;
  [COLUMNS.TYPE]: CostSettingProfile;
  [COLUMNS.CREATED]: number;
  [COLUMNS.CREATED_DISPLAY]: string;
  [COLUMNS.LAST_EDITED]: number;
  [COLUMNS.LAST_EDITED_DISPLAY]: string;
  [COLUMNS.ACTIONS]: CostSettingProfile;
  profile: CostSettingProfile;
};

interface SettingsCostLandingPageSignature {
  Args: {
    isError?: boolean;
    isLoading: boolean;
    isReadOnly?: boolean;
    profiles: CostSettingProfile[];
  };
}

export default class SettingsCostLandingPage extends Component<SettingsCostLandingPageSignature> {
  @service declare intl: Services['intl'];

  @tracked searchInputValue = '';

  initialSorts = [
    {
      valuePath: COLUMNS.TITLE,
      isAscending: true,
    },
  ];

  onSearchInputTask = task(
    { restartable: true },
    async (event: Event): Promise<void> => {
      await timeout();
      this.searchInputValue = (event.target as HTMLInputElement)?.value;
    },
  );

  get columns(): CustomEmberTableColumn[] {
    return [
      {
        name: this.intl.t('general.title'),
        valuePath: COLUMNS.TITLE,
        minWidth: 120,
        width: 150,
      },
      {
        name: this.intl.t('general.type'),
        valuePath: COLUMNS.TYPE,
        isSortable: false,
        minWidth: 150,
        textAlign: 'left' as const,
        tdComponent: SettingsCostTablesCellsTdTypes,
        width: 250,
      },
      {
        name: this.intl.t('general.created'),
        valuePath: COLUMNS.CREATED,
        displayValuePath: COLUMNS.CREATED_DISPLAY,
        textAlign: 'left' as const,
        minWidth: 100,
      },
      {
        name: this.intl.t('general.last_edited'),
        valuePath: COLUMNS.LAST_EDITED,
        displayValuePath: COLUMNS.LAST_EDITED_DISPLAY,
        textAlign: 'left' as const,
        minWidth: 100,
      },
      {
        name: '',
        valuePath: COLUMNS.ACTIONS,
        textAlign: 'right' as const,
        isSortable: false,
        isReadOnly: this.args.isReadOnly ?? false,
        tdComponent: SettingsCostTablesCellsTdWithActions,
      },
    ];
  }

  get emptyState(): { description: string; title: string } {
    if (this.searchInputValue) {
      return {
        title: this.intl.t('settings.cost.empty.no_results.title'),
        description: this.intl.t('settings.cost.empty.no_results.description'),
      };
    }

    return {
      title: this.intl.t('settings.cost.empty.title'),
      description: this.intl.t('settings.cost.empty.description'),
    };
  }

  get filteredRows(): Row[] {
    const query = this.searchInputValue.toLowerCase().trim();
    if (!query) {
      return this.rows;
    }

    return this.rows.filter((row) => {
      const name = (row.profile.name ?? '').toLowerCase();
      const description = (row.profile.description ?? '').toLowerCase();
      return name.includes(query) || description.includes(query);
    });
  }

  get rows(): Row[] {
    return this.args.profiles.map((profile) => {
      return {
        [COLUMNS.TITLE]: profile.name ?? '',
        [COLUMNS.TYPE]: profile,
        [COLUMNS.CREATED]: profile.createdAt.valueOf(),
        [COLUMNS.CREATED_DISPLAY]: profile.createdAt.format(DATE_FORMAT),
        [COLUMNS.LAST_EDITED]: profile.updatedAt.valueOf(),
        [COLUMNS.LAST_EDITED_DISPLAY]: profile.updatedAt.format(DATE_FORMAT),
        [COLUMNS.ACTIONS]: profile,
        profile,
      };
    });
  }

  <template>
    <SettingsPageTemplate
      @description={{t "settings.cost.page_description"}}
      @title={{t "settings.cost.title"}}
      @type="container"
    >
      <:default>
        <div class="flex flex-col gap-400" data-test-id="cost-profiles-list">
          <PrismCard @contentLayout="noPadding" class="overflow-hidden">
            <:header as |Header|>
              <Header.title>
                {{t "settings.cost.profiles.heading"}}
              </Header.title>
            </:header>

            <:headerAfter>
              <div class="flex gap-200">
                <PrismInput
                  @iconAfter={{hash name="search"}}
                  @name="search"
                  @onInput={{this.onSearchInputTask.perform}}
                  @placeholder={{t "general.search_placeholder"}}
                  @removePadding={{true}}
                  @type="text"
                  @value={{this.searchInputValue}}
                />

                {{#unless @isReadOnly}}
                  {{! Create opens the create modal — enabled in PIN-1244. }}
                  <PrismButton
                    @iconBefore="plus"
                    @isDisabled={{true}}
                    @label={{t "settings.cost.create"}}
                    data-test-id="cost-create-profile-button"
                  />
                {{/unless}}
              </div>
            </:headerAfter>

            <:content>
              <PrismTable
                @columns={{this.columns}}
                @enableAutoSortsUpdate={{true}}
                @fillMode="first-column"
                @initialSorts={{this.initialSorts}}
                @isLoading={{@isLoading}}
                @nrOfLoadingRows={{2}}
                @removeTableBorder={{true}}
                @rows={{this.filteredRows}}
                @widthConstraint="eq-container"
              >
                <:emptyState>
                  {{#if @isError}}
                    <div
                      class="flex w-full items-center justify-center"
                      data-test-id="cost-profiles-error-state"
                    >
                      <PrismMessage
                        @description={{t "general.error"}}
                        @illustration="100x100_critical"
                        @title={{t "general.error_title"}}
                      />
                    </div>
                  {{else}}
                    <div
                      class="flex w-full items-center justify-center"
                      data-test-id="cost-profiles-empty-state"
                    >
                      <PrismMessage
                        @description={{this.emptyState.description}}
                        @illustration="100x100_empty"
                        @title={{this.emptyState.title}}
                      />
                    </div>
                  {{/if}}
                </:emptyState>
              </PrismTable>
            </:content>
          </PrismCard>
        </div>
      </:default>
    </SettingsPageTemplate>
  </template>
}
